import { useState, useEffect } from 'react';
import {
  User,
  Phone,
  Mail,
  Calendar,
  MapPin,
  HeartPulse,
  Activity,
  X,
  Loader2,
  Shield,
  Save,
  AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const GENDERS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' }
];

const EditPatientModal = ({
  isOpen,
  onClose,
  leadId,
  initialData = {},
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    patientName: '',
    patientPhone: '',
    patientEmail: '',
    patientAge: '',
    patientGender: 'male',
    bloodGroup: '',
    dateOfBirth: '',
    emergencyContact: '',
    abhaId: '',
    city: '',
    patientAddress: '',
    treatmentRequired: '',
    disease: '',
    coverageMode: 'self_pay'
  });

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen && initialData) {
      setFormData({
        patientName: initialData.patientName || '',
        patientPhone: initialData.patientPhone || initialData.phone || '',
        patientEmail: initialData.patientEmail || initialData.email || '',
        patientAge: initialData.patientAge !== undefined && initialData.patientAge !== null ? String(initialData.patientAge) : '',
        patientGender: initialData.patientGender || 'male',
        bloodGroup: initialData.bloodGroup || '',
        dateOfBirth: initialData.dateOfBirth ? (typeof initialData.dateOfBirth === 'string' ? initialData.dateOfBirth.split('T')[0] : '') : '',
        emergencyContact: initialData.emergencyContact || '',
        abhaId: initialData.abhaId || '',
        city: initialData.city || '',
        patientAddress: initialData.patientAddress || '',
        treatmentRequired: initialData.treatmentRequired || initialData.procedure || '',
        disease: initialData.disease || '',
        coverageMode: initialData.coverageMode || 'self_pay'
      });
      setErrors({});
    }
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  const validate = () => {
    const errs = {};
    if (!formData.patientName.trim()) {
      errs.patientName = 'Patient full name is required';
    }
    const cleanPhone = formData.patientPhone.replace(/\D/g, '');
    if (!cleanPhone || cleanPhone.length < 10) {
      errs.patientPhone = 'Valid 10-digit mobile number is required';
    }
    if (formData.patientEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.patientEmail.trim())) {
      errs.patientEmail = 'Invalid email address';
    }
    if (formData.patientAge && (isNaN(formData.patientAge) || Number(formData.patientAge) < 0 || Number(formData.patientAge) > 125)) {
      errs.patientAge = 'Enter a valid age (0-125)';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validate()) return;
    if (!leadId) {
      toast.error('No Lead ID found to update');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        patientName: formData.patientName.trim(),
        patientPhone: formData.patientPhone.replace(/\D/g, '').slice(-10),
        patientEmail: formData.patientEmail.trim(),
        patientAge: formData.patientAge ? Number(formData.patientAge) : null,
        patientGender: formData.patientGender,
        bloodGroup: formData.bloodGroup || null,
        dateOfBirth: formData.dateOfBirth || null,
        emergencyContact: formData.emergencyContact.trim(),
        abhaId: formData.abhaId.trim(),
        city: formData.city.trim(),
        patientAddress: formData.patientAddress.trim(),
        treatmentRequired: formData.treatmentRequired.trim(),
        disease: formData.disease.trim() || formData.treatmentRequired.trim(),
        coverageMode: formData.coverageMode
      };

      const res = await api.patch(`/leads/${leadId}`, payload);
      toast.success('Patient details updated successfully!', { icon: '✅' });

      if (onSuccess) {
        onSuccess(res.data?.data || payload);
      }
      onClose();
    } catch (err) {
      console.error('Failed to update patient details:', err);
      toast.error(err?.response?.data?.message || 'Failed to update patient details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto animate-fadeIn">
      <div
        className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Ribbon */}
        <div className="bg-gradient-to-r from-[#143E78] to-[#1e5aa8] px-6 py-5 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-white border border-white/20">
              <User className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-black tracking-tight">Edit Patient Details</h2>
              <p className="text-xs text-blue-100/80 font-medium">
                Update patient identity, and contact information
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[78vh] overflow-y-auto">
          {/* Section 1: Demographics & Identity */}
          <div>
            <h3 className="text-[11px] font-black uppercase text-slate-400 tracking-wider mb-3 flex items-center gap-1.5">
              <User size={13} className="text-[#143E78]" />
              Primary Identity
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Full Name */}
              <div className="sm:col-span-2">
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Full Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.patientName}
                  onChange={(e) => handleChange('patientName', e.target.value)}
                  placeholder="e.g. Rahul Sharma"
                  className={`w-full px-3.5 py-2.5 bg-slate-50 border rounded-xl text-xs font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all ${errors.patientName ? 'border-rose-300 ring-2 ring-rose-100' : 'border-slate-200 focus:border-[#143E78]'
                    }`}
                />
                {errors.patientName && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1 flex items-center gap-1">
                    <AlertCircle size={11} /> {errors.patientName}
                  </p>
                )}
              </div>

              {/* Phone Number */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Contact Number (10 digits) <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 select-none">
                    +91
                  </span>
                  <input
                    type="tel"
                    maxLength={10}
                    value={formData.patientPhone}
                    onChange={(e) => handleChange('patientPhone', e.target.value.replace(/\D/g, ''))}
                    placeholder="9876543210"
                    className={`w-full pl-12 pr-3.5 py-2.5 bg-slate-50 border rounded-xl text-xs font-mono font-bold text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all ${errors.patientPhone ? 'border-rose-300 ring-2 ring-rose-100' : 'border-slate-200 focus:border-[#143E78]'
                      }`}
                  />
                </div>
                {errors.patientPhone && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1 flex items-center gap-1">
                    <AlertCircle size={11} /> {errors.patientPhone}
                  </p>
                )}
              </div>

              {/* Email Address */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Email Address</label>
                <input
                  type="email"
                  value={formData.patientEmail}
                  onChange={(e) => handleChange('patientEmail', e.target.value)}
                  placeholder="patient@example.com"
                  className={`w-full px-3.5 py-2.5 bg-slate-50 border rounded-xl text-xs font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all ${errors.patientEmail ? 'border-rose-300 ring-2 ring-rose-100' : 'border-slate-200 focus:border-[#143E78]'
                    }`}
                />
                {errors.patientEmail && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1 flex items-center gap-1">
                    <AlertCircle size={11} /> {errors.patientEmail}
                  </p>
                )}
              </div>

              {/* Age */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Age (Years)</label>
                <input
                  type="number"
                  min="0"
                  max="125"
                  value={formData.patientAge}
                  onChange={(e) => handleChange('patientAge', e.target.value)}
                  placeholder="e.g. 42"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-[#143E78] focus:ring-2 focus:ring-blue-500/20 transition-all"
                />
              </div>

              {/* Gender */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Gender</label>
                <select
                  value={formData.patientGender}
                  onChange={(e) => handleChange('patientGender', e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:bg-white focus:border-[#143E78] focus:ring-2 focus:ring-blue-500/20 transition-all cursor-pointer"
                >
                  {GENDERS.map((g) => (
                    <option key={g.value} value={g.value}>
                      {g.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Blood Group */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Blood Group</label>
                <select
                  value={formData.bloodGroup}
                  onChange={(e) => handleChange('bloodGroup', e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:bg-white focus:border-[#143E78] focus:ring-2 focus:ring-blue-500/20 transition-all cursor-pointer"
                >
                  <option value="">Select Blood Group</option>
                  {BLOOD_GROUPS.map((bg) => (
                    <option key={bg} value={bg}>
                      {bg}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date of Birth */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Date of Birth</label>
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:bg-white focus:border-[#143E78] focus:ring-2 focus:ring-blue-500/20 transition-all cursor-pointer"
                />
              </div>
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Section 2: Contact, Location & Identifiers */}
          <div>
            <h3 className="text-[11px] font-black uppercase text-slate-400 tracking-wider mb-3 flex items-center gap-1.5">
              <MapPin size={13} className="text-[#143E78]" />
              Location & Emergency Contacts
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Emergency Contact */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Emergency Contact Person / Phone</label>
                <input
                  type="text"
                  value={formData.emergencyContact}
                  onChange={(e) => handleChange('emergencyContact', e.target.value)}
                  placeholder="e.g. Ramesh Sharma (Brother - 9876543210)"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-[#143E78] focus:ring-2 focus:ring-blue-500/20 transition-all"
                />
              </div>

              {/* ABHA ID */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">ABHA Health ID</label>
                <input
                  type="text"
                  value={formData.abhaId}
                  onChange={(e) => handleChange('abhaId', e.target.value)}
                  placeholder="e.g. 12-3456-7890-1234"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-[#143E78] focus:ring-2 focus:ring-blue-500/20 transition-all"
                />
              </div>

              {/* City / State */}
              <div className="sm:col-span-2">
                <label className="text-xs font-bold text-slate-700 block mb-1">City / Region</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="e.g. Bhubaneswar, Odisha"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-[#143E78] focus:ring-2 focus:ring-blue-500/20 transition-all"
                />
              </div>

              {/* Residential Address */}
              <div className="sm:col-span-2">
                <label className="text-xs font-bold text-slate-700 block mb-1">Residential Full Address</label>
                <textarea
                  rows={2}
                  value={formData.patientAddress}
                  onChange={(e) => handleChange('patientAddress', e.target.value)}
                  placeholder="Plot / House No, Street, Landmark, Pincode"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-[#143E78] focus:ring-2 focus:ring-blue-500/20 transition-all resize-none"
                />
              </div>
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Section 3: Clinical & Coverage */}
          <div>
            <h3 className="text-[11px] font-black uppercase text-slate-400 tracking-wider mb-3 flex items-center gap-1.5">
              <HeartPulse size={13} className="text-[#143E78]" />
              Procedure & Coverage Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Planned Procedure */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Planned Procedure / Treatment</label>
                <input
                  type="text"
                  value={formData.treatmentRequired}
                  onChange={(e) => handleChange('treatmentRequired', e.target.value)}
                  placeholder="e.g. Laparoscopic Cholecystectomy / Knee Replacement"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-[#143E78] focus:ring-2 focus:ring-blue-500/20 transition-all"
                />
              </div>

              {/* Payment / Coverage Mode */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Coverage / Payment Mode</label>
                <select
                  value={formData.coverageMode}
                  onChange={(e) => handleChange('coverageMode', e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:bg-white focus:border-[#143E78] focus:ring-2 focus:ring-blue-500/20 transition-all cursor-pointer"
                >
                  <option value="self_pay">Self Pay (Cash / Direct)</option>
                  <option value="insurance">Private Insurance (TPA / Mediclaim)</option>
                  <option value="ayushman">Ayushman Bharat (PM-JAY)</option>
                  <option value="corporate">Corporate / Employer Coverage</option>
                </select>
              </div>
            </div>
          </div>

          {/* Form Actions Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2.5 text-xs font-bold text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-all cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 bg-[#143E78] hover:bg-[#0f2e5a] text-white rounded-xl text-xs font-bold shadow-md shadow-blue-950/20 flex items-center gap-2 transition-all cursor-pointer disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  <span>Saving Changes...</span>
                </>
              ) : (
                <>
                  <Save size={14} />
                  <span>Save Patient Details</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPatientModal;
